import React, { useContext, useEffect, useState } from "react";
import { Card, Table, Button, Spinner } from "flowbite-react"; // Added Spinner
import { Store } from "../context/store";
import { IoMdDownload, IoMdArrowBack } from "react-icons/io";
import { Link } from "react-router-dom";
import { usePDF } from "react-to-pdf";
import { useNavigate } from "react-router-dom";
import apiRequest from "../utils/apiRequest";
import { formatDate } from "../utils/date";

const PreviewReceipts = () => {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  let [invoice, setInvoice] = store.invoice;
  let [loading, setLoading] = useState(false); // New loading state for the button
  const navigate = useNavigate();
  const [error, setError] = useState(null); // New state to display API errors

  const location = window.location.pathname;

  useEffect(() => {
    if (!invoice || !invoice.rows || invoice.rows.length === 0) {
      if (location === "/preview") {
        navigate("/receipts");
      } else {
        navigate("/view-receipts");
      }
    }
  }, [setInvoice, invoice, navigate, location]);

  // Calculate total amount
  const totalAmount = Object.values(invoice?.rows || {}).reduce(
    (total, row) => total + (Number(row.amount) || 0),
    0,
  );

  const receiptRows = (invoice?.rows || []).map((row) => {
    const amount = Number(row.amount) || 0;

    return {
      ...row,
      quantity: 1,
      ordered: 1,
      rate: amount,
      amount,
    };
  });

  // PDF configuration
  const { toPDF, targetRef } = usePDF({
    filename: `Royalmines-Receipt-${invoice.receiptNumber || "Draft"}-${
      invoice.invoiceName
    }.pdf`,
  });

  // 1. New async function to handle API call and download
  let downloadReceipt = async () => {
    setError(null);
    setLoading(true);
    let token = localStorage.getItem("royal-token");

    // Construct the payload from the current invoice state
    const payload = {
      date: invoice.date,
      email: invoice.email,
      invoiceAddress: invoice.invoiceAddress,
      invoiceName: invoice.invoiceName,
      officeAddress: invoice.officeAddress,
      phone: invoice.phone,
      project: invoice.project,
      rows: receiptRows,
    };

    try {
      const url = `${baseUrl}/receipt`;
      const response = await apiRequest(url, "POST", payload, token);
      // 3. Extract the receiptNumber from the response and update the state
      const { receiptNumber } = response.data; // Assuming the response object has a receiptNumber key

      // Update the invoice state to include the receiptNumber
      setInvoice((prev) => ({
        ...prev,
        receiptNumber: receiptNumber,
        // Ensure the receipt component shows the number before generating PDF
      }));

      // **Important:** Wait a moment for state change and component re-render
      // to display the receiptNumber on the PDF content (targetRef).
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 4. Trigger the PDF download using usePDF
      toPDF();

      // 5. Cleanup and navigation after successful download
      setInvoice({ rows: [] });
      navigate("/receipts");
    } catch (err) {
      console.error("Error creating receipt:", err);
      setError(err.message || "Failed to generate receipt. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  let downloadOldReceipt = async () => {
    setLoading(true);
    try {
      toPDF();
    } catch (err) {
      setError(err.message || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <title>Preview Receipt</title>
      <meta
        name="description"
        content="Preview Page to the Royalmines Property Office"
      />
      <div className="container mx-auto">
        <div className="container-fluid mx-auto">
          <div data-aos="slide-left">
            <div className="flex flex-wrap gap-2 mt-5 justify-between align-middle">
              <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
                Preview Receipts
              </h5>

              {/* Display API Error */}
              {error && (
                <p className="text-red-500 font-medium my-2 w-full text-center">
                  {error}
                </p>
              )}

              {/* Updated Button to use the new async handler and loading state */}
              <Button
                onClick={
                  location === "/preview" ? downloadReceipt : downloadOldReceipt
                }
                color="success"
                disabled={loading || !invoice.rows || invoice.rows.length === 0}
              >
                {loading ? (
                  <Spinner size="sm" light={true} className="me-2" />
                ) : (
                  <IoMdDownload className="me-2" />
                )}
                {loading ? "Generating..." : "Download Receipt"}
              </Button>

              <Button
                color="info"
                as={Link}
                to={location === "/preview" ? "/receipts" : "/view-receipts"}
              >
                <IoMdArrowBack className="me-2" />
                Go Back
              </Button>
            </div>
            <div ref={targetRef}>
              <Card className="rounded-xl my-4 receipt uppercase">
                <div className="receipt1">
                  {/* receipt headers */}
                  <div className="flex justify-between">
                    {/* company name */}
                    <div className="font-extrabold uppercase">
                      <h3>royalmines Property limited</h3>
                      <h3>plot 251/252 herbert macaulay way,</h3>
                      <h3>millennium plaza cbd,</h3>
                      <h3>abuja.</h3>
                    </div>
                    {/* sales order */}
                    <div>
                      <h2 className="capitalize text-3xl font-extrabold">
                        E-RECEIPT
                      </h2>
                      <table className="border-2 border-black rounded mt-4">
                        <tbody className="text-center">
                          <tr>
                            <td className="border-b-2 border-r-2 border-black w-[250px]">
                              <h3 className="font-bold">Date</h3>
                            </td>
                            <td className="border-b-2 border-black px-5 w-[150px]">
                              <h3 className="py-2 font-bold">Receipt No.</h3>
                            </td>
                          </tr>
                          <tr>
                            <td className="border-r-2 border-black px-5">
                              <p className="">{formatDate(invoice?.date)}</p>
                            </td>
                            <td>
                              {/* Display the new receiptNumber */}
                              <p className="py-2 font-bold text-lg">
                                {invoice?.receiptNumber || "DRAFT"}
                              </p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ... rest of the receipt content (Name/Address, Items Table) ... */}
                  {/* (Only the receipt number display was updated here for brevity) */}

                  {/* name and address */}
                  <div className="flex gap-10 justify-between mt-10">
                    <div className="border-2 border-black w-1/2 p-3">
                      <h3 className="border-b-2 border-black pb-2 mb-2">
                        Name / Address
                      </h3>
                      <ul className="uppercase">
                        <li>{invoice?.invoiceName}</li>
                        {invoice?.invoiceAddress
                          ?.split(",")
                          .map((addressPart, index) => (
                            <li key={index}>{addressPart.trim()}</li>
                          ))}
                      </ul>
                    </div>

                    <div className="border-2 border-black w-1/2 p-3">
                      <h3 className="border-b-2 border-black pb-2 mb-2">
                        Email / Office Address
                      </h3>
                      {/* Note: invoice?.shipName and invoice?.shipAddress are likely wrong here based on your payload structure. 
                                            I'll use the correct keys from your payload: invoice.email and invoice.officeAddress */}
                      <ul className="uppercase">
                        <li className="lowercase">{invoice?.email}</li>
                        {invoice?.officeAddress
                          ?.split(",")
                          .map((addressPart, index) => (
                            <li key={index}>{addressPart.trim()}</li>
                          ))}
                      </ul>
                    </div>
                  </div>

                  {/* invoice body */}
                  <div className="flex justify-end">
                    <table className="border-2 border-black rounded mt-4">
                      <tbody className="text-center">
                        <tr>
                          <td className="border-b-2 border-r-2 border-black w-[150px]">
                            <h3 className="font-bold">Phone</h3>
                          </td>
                          <td className="border-b-2 border-black px-5 w-[400px]">
                            <h3 className="py-2 font-bold">Project</h3>
                          </td>
                        </tr>
                        <tr>
                          <td className="border-r-2 border-black px-5">
                            {/* Note: Using invoice?.phone based on your payload, not invoice?.po */}
                            <p className="min-h-10 min-w-20 pt-2">
                              {invoice?.phone}
                            </p>
                          </td>
                          <td className="px-5">
                            <p className="min-h-10 min-w-20 pt-2 text-left">
                              {invoice?.project}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Items Table */}
                  <div className="overflow-x-auto">
                    <Table className="border-2 border-black text-xs">
                      <Table.Head>
                        <Table.HeadCell>Item</Table.HeadCell>
                        <Table.HeadCell colSpan={3}>Description</Table.HeadCell>
                        <Table.HeadCell>Amount</Table.HeadCell>
                      </Table.Head>

                      <Table.Body className="divide-y divide-x text-black text-xl">
                        {receiptRows?.map((e, i) => {
                          return (
                            <Table.Row key={i}>
                              <Table.Cell className="whitespace-nowrap text-ellipsis">
                                {e.item}
                              </Table.Cell>
                              <Table.Cell colSpan={3}>
                                {e.description}
                              </Table.Cell>
                              <Table.Cell>
                                &#8358;
                                {e.amount.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}

                        {/* Empty rows removed for brevity, keep if needed for styling */}

                        <Table.Row>
                          <Table.Cell colSpan={3}></Table.Cell>
                          <Table.Cell className="font-extrabold text-2xl">
                            Total
                          </Table.Cell>
                          <Table.Cell className="font-bold text-lg">
                            &#8358;
                            {totalAmount?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </div>

                  {/* Authorization Section */}
                  <div className="mt-16 flex justify-between items-end gap-10 flex-col">
                    {/* Authorized By */}
                    <div className="w-1/3 text-center">
                      <div className="border-t-2 border-black pt-2">
                        <p className="font-bold">Authorised By</p>
                        {/* <p className="text-sm mt-1">Royalmines Property Ltd</p> */}
                      </div>
                    </div>

                    {/* Signature */}
                    <div className="w-1/3 text-center">
                      <div className="border-t-2 border-black pt-2">
                        <p className="font-bold">Signature</p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="w-1/3 text-center">
                      <div className="border-t-2 border-black pt-2">
                        <p className="font-bold">Date</p>
                        {/* <p className="text-sm mt-1">{formatDate(new Date())}</p> */}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewReceipts;
